import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';

// Components
import Login from './components/Login.jsx';
import HostessLogin from './components/HostessLogin.jsx';
import MainLayout from './components/MainLayout.jsx';
import Intranet from './components/Intranet.jsx';
import EventManagement from './components/EventManagement.jsx';
import EventDetails from './components/EventDetails.jsx';
import TischVorlagen from './components/TischVorlagen.jsx';
import CheckInInterface from './components/CheckInInterface.jsx';
import PublicView from './components/PublicView.jsx';
import AdminUsers from './components/AdminUsers.jsx';
import AdminFeatures from './components/AdminFeatures.jsx';
import AdminAuditLogs from './components/AdminAuditLogs.jsx';
import Tickets from './components/Tickets.jsx';
import AdminTickets from './components/AdminTickets.jsx';
import Logenbelegung from './components/Logenbelegung.jsx';
import AdminLogen from './components/AdminLogen.jsx';
import Anfrageportal from './components/Anfrageportal.jsx';
import AdminAnfragen from './components/AdminAnfragen.jsx';
import Kontakte from './components/Kontakte.jsx';
import AdminKontakte from './components/AdminKontakte.jsx';

import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Setup axios defaults
axios.defaults.baseURL = API;
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Axios error:', error.response?.status, error.response?.data);
    
    // Only redirect on certain 401 errors, not all
    if (error.response?.status === 401 && error.response?.data?.detail?.includes('credentials')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState('light');

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    // Apply theme to document
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(theme);
  }, [theme]);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get('/user/me');
      setUser(response.data);
      // Apply user's theme preference
      if (response.data.settings?.theme) {
        setTheme(response.data.settings.theme);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (userData, token, isHostess = false) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    if (userData.settings?.theme) {
      setTheme(userData.settings.theme);
    }
    toast.success('Erfolgreich angemeldet!');
    
    // Fetch full user data with accessible_features
    if (!isHostess) {
      try {
        const response = await axios.get('/user/me');
        setUser(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
      } catch (error) {
        console.error('Failed to fetch full user data:', error);
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setTheme('light');
    toast.success('Erfolgreich abgemeldet!');
  };

  const updateTheme = async (newTheme) => {
    setTheme(newTheme);
    try {
      await axios.patch('/user/settings', { theme: newTheme });
    } catch (error) {
      console.error('Failed to save theme preference:', error);
    }
  };

  const hasFeatureAccess = (featureId) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    if (user.role === 'hostess' && featureId === 'tischplan') return true;
    return user.accessible_features?.some(f => f.id === featureId);
  };

  const hasFeatureAdmin = (featureId) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return user.accessible_features?.some(
      f => f.id === featureId && f.permission_level === 'admin'
    );
  };

  const authContextValue = {
    user,
    setUser,
    theme,
    setTheme: updateTheme,
    hasFeatureAccess,
    hasFeatureAdmin,
    handleLogout
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <img 
            src="https://www.haie.de/wp-content/uploads/2021/11/del-logo-kec.svg" 
            alt="Kölner Haie" 
            className="w-20 h-20 mx-auto mb-4 animate-pulse"
          />
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#e30613] border-t-transparent mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={authContextValue}>
      <div className={`App ${theme}`}>
        <BrowserRouter>
          <Routes>
            {/* Public Routes (no authentication required) */}
            <Route path="/public/event/:token" element={<PublicView />} />
            
            {!user ? (
              <>
                <Route path="/login" element={<Login onLogin={handleLogin} />} />
                <Route path="/hostess-login" element={<HostessLogin onLogin={handleLogin} />} />
                <Route path="*" element={<Navigate to="/login" replace />} />
              </>
            ) : (
              <Route element={<MainLayout user={user} onLogout={handleLogout} />}>
                {/* Home / Intranet */}
                <Route 
                  path="/" 
                  element={
                    user.role === 'hostess' 
                      ? <Navigate to="/tischplan" replace /> 
                      : hasFeatureAccess('intranet') 
                        ? <Intranet />
                        : <Navigate to="/tischplan" replace />
                  } 
                />
                
                {/* Intranet Routes */}
                {hasFeatureAccess('intranet') && (
                  <Route path="/intranet" element={<Intranet />} />
                )}
                
                {/* Tischplan Routes */}
                {hasFeatureAccess('tischplan') && (
                  <>
                    <Route path="/tischplan" element={<EventManagement user={user} onLogout={handleLogout} />} />
                    <Route path="/tischplan/:eventId" element={<EventDetails user={user} onLogout={handleLogout} />} />
                    <Route path="/tischplan/:eventId/checkin" element={<CheckInInterface user={user} onLogout={handleLogout} />} />
                    <Route path="/tischvorlagen" element={<TischVorlagen user={user} onLogout={handleLogout} />} />
                    {/* Legacy routes for backwards compatibility */}
                    <Route path="/events" element={<Navigate to="/tischplan" replace />} />
                    <Route path="/events/:eventId" element={<EventDetails user={user} onLogout={handleLogout} />} />
                    <Route path="/events/:eventId/checkin" element={<CheckInInterface user={user} onLogout={handleLogout} />} />
                  </>
                )}
                
                {/* Tickets Routes */}
                {hasFeatureAccess('tickets') && (
                  <Route path="/tickets" element={<Tickets />} />
                )}
                
                {/* Logenbelegung Routes */}
                {hasFeatureAccess('logenbelegung') && (
                  <Route path="/logenbelegung" element={<Logenbelegung />} />
                )}
                
                {/* Anfrageportal Routes */}
                {hasFeatureAccess('anfrageportal') && (
                  <Route path="/anfragen" element={<Anfrageportal />} />
                )}
                
                {/* Kontaktverwaltung Routes */}
                {hasFeatureAccess('kontaktverwaltung') && (
                  <Route path="/kontakte" element={<Kontakte />} />
                )}
                
                {/* Admin Routes - Global admin only */}
                {user.role === 'admin' && (
                  <>
                    <Route path="/admin/users" element={<AdminUsers />} />
                    <Route path="/admin/features" element={<AdminFeatures />} />
                    <Route path="/admin/audit-logs" element={<AdminAuditLogs />} />
                  </>
                )}
                
                {/* Feature-specific admin routes - role=admin OR feature-level admin */}
                {hasFeatureAdmin('tickets') && (
                  <Route path="/admin/tickets" element={<AdminTickets />} />
                )}
                {hasFeatureAdmin('logenbelegung') && (
                  <Route path="/admin/logen" element={<AdminLogen />} />
                )}
                {hasFeatureAdmin('anfrageportal') && (
                  <Route path="/admin/anfragen" element={<AdminAnfragen />} />
                )}
                {hasFeatureAdmin('kontaktverwaltung') && (
                  <Route path="/admin/kontakte" element={<AdminKontakte />} />
                )}
                
                {/* Catch all */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            )}
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </AuthContext.Provider>
  );
}

export default App;
